import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles, Plus, Gift, CloudSun } from "lucide-react";
import { categorySlug, displayCat } from "@/lib/readoo";

const CHIP_BASE = "shrink-0 inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200";
const CHIP_ACTIVE = "bg-brand text-onBrand border-brand";
const CHIP_INACTIVE = "bg-surfaceSecondary text-onSurfaceSecondary border-hair hover:border-hairStrong hover:text-onSurface";

export function CategoryChips({ categories = [], active = null, showForYou = false, forYouActive = false, showCreateFeed = false }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 4);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [update, categories]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <div className="sticky top-16 z-30 bg-surface/95 backdrop-blur-sm border-b border-hair">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Left control + fade */}
        {canLeft && (
          <>
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-surface to-transparent z-10 hidden md:block" />
            <button
              type="button"
              data-testid="chips-scroll-left"
              onClick={() => scrollBy(-1)}
              aria-label="Scorri a sinistra"
              className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 items-center justify-center rounded-full border border-hair bg-surfaceSecondary text-onSurfaceSecondary hover:text-brand hover:border-brand/50 shadow-md transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
          </>
        )}

        <div ref={scrollerRef} className="flex items-center gap-2 overflow-x-auto no-scrollbar py-3 scroll-smooth">
          {/* Chip di testa: "Per te" (utente con interessi) OPPURE "+ Crea il tuo feed" (utente senza interessi) */}
          {showForYou ? (
            <Link
              to="/per-te"
              data-testid="category-chip-per-te"
              className={`${CHIP_BASE} ${forYouActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
            >
              <Sparkles size={13} /> Per te
            </Link>
          ) : showCreateFeed ? (
            <Link
              to="/profilo#interessi"
              data-testid="category-chip-create-feed"
              className={`${CHIP_BASE} bg-brandTertiary text-brand border-brand/40 hover:border-brand`}
            >
              <Plus size={13} /> Crea il tuo feed
            </Link>
          ) : null}

          {/* "Ultimissime" SEMPRE presente, subito dopo il chip di testa, in tutti gli stati */}
          <Link
            to="/"
            data-testid="category-chip-ultimissime"
            className={`${CHIP_BASE} ${!forYouActive && (active == null || active === "Ultimissime") ? CHIP_ACTIVE : CHIP_INACTIVE}`}
          >
            Ultimissime
          </Link>

          {categories.filter((name) => name !== "Ultimissime").map((name) => {
            const slug = categorySlug(name);
            const isActive = active === name;
            return (
              <Link
                key={name}
                to={`/categoria/${slug}`}
                data-testid={`category-chip-${slug}`}
                className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
              >
                {displayCat(name)}
              </Link>
            );
          })}

          <Link
            to="/meteo"
            data-testid="category-chip-meteo"
            className={`${CHIP_BASE} ${active === "Meteo" ? CHIP_ACTIVE : CHIP_INACTIVE}`}
          >
            <CloudSun size={13} /> Meteo
          </Link>

          <Link
            to="/vantaggi"
            data-testid="category-chip-vantaggi"
            className={`${CHIP_BASE} bg-brandTertiary text-brand border-brand/40 hover:border-brand`}
          >
            <Gift size={13} /> Vantaggi
          </Link>
        </div>

        {/* Right control + fade */}
        {canRight && (
          <>
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-surface to-transparent z-10 hidden md:block" />
            <button
              type="button"
              data-testid="chips-scroll-right"
              onClick={() => scrollBy(1)}
              aria-label="Scorri a destra"
              className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-20 w-8 h-8 items-center justify-center rounded-full border border-hair bg-surfaceSecondary text-onSurfaceSecondary hover:text-brand hover:border-brand/50 shadow-md transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
